import { AiGeneratedExpense } from 'src/services/receipts'
import { PaymentAccount } from 'src/services/payment-accounts'
import { resolvePaymentAccountRef } from 'src/utils/resolve-payment-account-ref'

interface CategoryOption {
  id: string
  value: string
  label: string
}

interface PopulateExpenseOptions {
  data: AiGeneratedExpense
  receiptId: string
  form: any
  createFormFieldChangeHandler: (field: any) => (value: any) => void
  expenseCategories?: CategoryOption[] | null
  cashFlowTypes?: [string, string][] | null
  organizations?: { results: { id: string; name: string }[] } | null
  paymentAccounts?: { results: PaymentAccount[] } | null
  reports?: { results: { id: string; name: string }[] } | null
  paymentAccountLabel: (pa: PaymentAccount) => string
}

export function populateExpenseFromAi({
  data,
  receiptId,
  form,
  createFormFieldChangeHandler,
  expenseCategories,
  cashFlowTypes,
  organizations,
  paymentAccounts,
  reports,
  paymentAccountLabel,
}: PopulateExpenseOptions) {
  if (data.name) createFormFieldChangeHandler(form._name)(data.name)
  if (data.amount) {
    createFormFieldChangeHandler(form.amount)(data.amount.toString())
    createFormFieldChangeHandler(form.amountFloat)(data.amount)
  }
  if (data.amountCurrency) createFormFieldChangeHandler(form.amountCurrency)(data.amountCurrency)
  if (data.notes) createFormFieldChangeHandler(form.notes)(data.notes)
  if (data.expenseDate) createFormFieldChangeHandler(form.expenseDate)(data.expenseDate)
  if (data.providedId) createFormFieldChangeHandler(form.providedId)(data.providedId)

  // Auto-select category: prefer the AI-resolved expense_category id (mirrors
  // organization/payment account resolution), fall back to matching the
  // legacy category value against the options list.
  if (expenseCategories) {
    const categoryOption = data.expenseCategory
      ? expenseCategories.find((cat) => cat.id === data.expenseCategory)
      : data.category
      ? expenseCategories.find((cat) => cat.value === data.category)
      : undefined
    if (categoryOption) {
      createFormFieldChangeHandler(form.categoryRef)({
        value: categoryOption.value,
        label: categoryOption.label,
        categoryId: categoryOption.id,
      })
    }
  }

  if (data.cashFlowType && cashFlowTypes) {
    const cashFlowOption = cashFlowTypes.find((type) => type[0] === data.cashFlowType)
    if (cashFlowOption) {
      createFormFieldChangeHandler(form.cashFlowTypeRef)({
        value: cashFlowOption[0],
        label: cashFlowOption[1],
      })
    }
  }

  // Auto-select organization: prefer resolved UUID, fall back to name match
  if (organizations?.results) {
    const matchingOrg = data.organization
      ? organizations.results.find((org) => org.id === data.organization)
      : data.organizationName
      ? organizations.results.find(
          (org) => org.name.toLowerCase() === data.organizationName?.toLowerCase(),
        )
      : undefined
    if (matchingOrg) {
      createFormFieldChangeHandler(form.organizationRef)({
        value: matchingOrg.id,
        label: matchingOrg.name,
      })
    }
  }

  // Auto-select payment account: prefer resolved UUID, fall back to hint match
  const paymentAccountRef = resolvePaymentAccountRef({
    paymentAccountId: data.paymentAccount,
    paymentAccountHint: data.paymentAccountHint,
    paymentAccounts,
    paymentAccountLabel,
  })
  if (paymentAccountRef) {
    createFormFieldChangeHandler(form.paymentAccountRef)(paymentAccountRef)
  }

  // Auto-select reports from resolved UUIDs
  if (data.reports?.length && reports?.results) {
    const matchedReports = data.reports
      .map((reportId) => reports.results.find((r) => r.id === reportId))
      .filter(Boolean)
    if (matchedReports.length > 0) {
      createFormFieldChangeHandler(form.reportsRef)(
        matchedReports.map((r) => ({ value: r!.id, label: r!.name })),
      )
    }
  }

  // Automatically add the receipt to the form if not already added
  const currentReceipts = form.receipts.value || []
  if (!currentReceipts.includes(receiptId)) {
    createFormFieldChangeHandler(form.receipts)([...currentReceipts, receiptId])
  }
}
